<template>
  <div
    data-cy="scroll-tracker"
    class="group relative h-fit w-10 flex flex-col items-end self-start space-y-1.5 select-none"
  >
    <placement-content-anchor-navigation
      class="absolute top-0 right-8 hidden group-hover:block"
      :sections
      :scroll-container
      :active-indices
    />
    <span
      v-for="(item, index) in trackedItems"
      :key="item.key"
      data-cy="scroll-tracker-bar"
      :data-item-type="item.type"
      :class="[
        'rounded-md h-[3px] transition-colors duration-200',
        item.type === 'header' ? 'w-8' : 'w-5',
        activeIndices.has(index) && item.type === 'table'
          ? 'bg-primary'
          : 'bg-gray-400',
      ]"
    />
  </div>
</template>

<script setup lang="ts">
import {
  computed,
  onBeforeUnmount,
  ref,
  shallowRef,
  triggerRef,
  watch,
} from "vue";

import PlacementContentAnchorNavigation from "@/components/deals/placement/PlacementContentAnchorNavigation.vue";

import type { AnchorNavigationSection } from "@/components/deals/placement/PlacementContentAnchorNavigation.vue";

const props = defineProps<{
  sections: AnchorNavigationSection[];
  scrollContainer: HTMLElement | null;
}>();
const visibilityMap = shallowRef<Map<number, boolean>>(new Map());

let currentObserverId = 0;
// Use a Map for O(1) element-to-index lookup instead of indexOf which is O(n) and fragile
let elementToIndexMap = new Map<HTMLElement, number>();
const intersectionObserver = ref<IntersectionObserver | null>(null);

const trackedItems = computed(() => {
  const items: Array<{
    type: "header" | "table";
    element: HTMLElement | null;
    key: string;
  }> = [];

  props.sections.forEach((section, sIndex) => {
    items.push({
      type: "header",
      element: section.headerElement,
      key: `header-${sIndex}-${section.label}`,
    });

    section.funders.forEach((funder, fIndex) => {
      items.push({
        type: "table",
        element: funder.tableElement,
        key: `table-${sIndex}-${fIndex}-${funder.name}`,
      });
    });
  });

  return items;
});

// Computed fingerprint to detect when elements actually change (not just length)
const elementsFingerprint = computed(() => {
  return trackedItems.value.map((item) => item.key).join("|");
});

const activeIndices = computed(() => {
  const indices = new Set<number>();
  visibilityMap.value.forEach((isVisible, index) => {
    if (isVisible) {
      indices.add(index);
    }
  });
  return indices;
});

const setupIntersectionObserver = () => {
  const items = trackedItems.value;

  // Disconnect old observer first
  intersectionObserver.value?.disconnect();

  currentObserverId++;
  const observerId = currentObserverId;

  // Clear all state when rebuilding
  visibilityMap.value = new Map();
  elementToIndexMap = new Map<HTMLElement, number>();

  if (!props.scrollContainer) {
    return;
  }

  // Build element-to-index map and collect valid elements
  const validElements: HTMLElement[] = [];
  items.forEach((item, index) => {
    if (item.element) {
      elementToIndexMap.set(item.element, index);
      validElements.push(item.element);
    }
  });

  if (validElements.length === 0) {
    return;
  }

  // Capture the map reference for the callback closure
  const currentElementMap = elementToIndexMap;

  intersectionObserver.value = new IntersectionObserver(
    (entries) => {
      if (observerId !== currentObserverId) {
        return;
      }

      entries.forEach((entry) => {
        const index = currentElementMap.get(entry.target as HTMLElement);
        if (index === undefined) {
          return;
        }
        visibilityMap.value.set(index, entry.isIntersecting);
      });

      triggerRef(visibilityMap);
    },
    { root: props.scrollContainer, threshold: 0.1 }
  );

  // Start observing all valid elements
  validElements.forEach((element) => {
    intersectionObserver.value!.observe(element);
  });
};

// Use flush: 'post' to ensure this runs AFTER Vue has updated the DOM
// This is critical because we need the actual DOM element references to be current
watch(
  () => [elementsFingerprint.value, props.scrollContainer],
  () => setupIntersectionObserver(),
  { immediate: true, flush: "post" }
);

onBeforeUnmount(() => {
  intersectionObserver.value?.disconnect();
});
</script>
